import Immutable, { fromJS } from "immutable"
import randomstring from "randomstring"
import actionTypes from "sheets/sheet-action-types"
import customFieldActionTypes from "shared/customFields/action-types"
import sortTypes from "sheets/sort-types"
import urls from "imports/desktopPaths"
import sheetConstants from "sheets/constants"
import { GRAPH_TYPES } from "shared/imports/sharedConstants"
import swal from "sweetalert"
import equal from "deep-equal"
import searchifyActionTypes from "shared/search/searchify/action-types"
import { idFromResourceUri } from "shared/imports/sharedHelperFunctions"
import { createSheetKey } from "sheets/selectors/sheet-selectors"

/**
 * Is the provided search value a "clear date" search
 * Most column search values are either integers or strings, but for dates we pass an object with startDate and endDate
 * keys. The date component sends null values for empty values
 * @param {Integer|String|Object} searchValue - the searching value taken from the redux action
 * @returns {Boolean}
 */
const isEmptyDateSearch = (searchValue) =>
    typeof searchValue === "object" && equal(searchValue, { startDate: null, endDate: null })

// the shell of a sheet object for the api
export function createBlankSheet(kwargs = {}) {
    return Immutable.fromJS({
        can_fit_in_memory: true, // this needs to be specified for the hideMapButton boolean to work
        columns: [],
        data_type: DjangIO.app.models.QuorumDataType.person.value,
        is_public: false,
        is_resource_table: false,
        limited_to_teams: Userdata.default_limited_to_teams,
        is_limited_to_creator: false,
        organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id), // TODO-TM this field must be set on prod
        projects: [],
        searchifyCount: 0,
        public_slug: null,
        row_data: [],
        row_count: 0,
        rows: {},
        show_map: false,
        passwordRequired: false,
        external_regions: [],
        external_custom_region_selection: null,
    }).merge(kwargs)
}

/**
 * This function returns the shell of a sheet, to be set in the store.
 * gets called on SHEET_INIT
 * @param {Object} kwargs - additional parameters you'd like to merge into the object
 * if the parameters contain a property 'sheet', this will be destructured and passed
 * to createBlankSheet, because otherwise 'sheet' will override the return value of
 * createBlankSheet, due to the behavior of .merge
 * @returns {Immutable Map}
 */
export const initializeSheet = ({ sheet, ...kwargs } = {}) =>
    Immutable.fromJS({
        added_ids: [],
        canAddColumns: false,
        canDownload: false,
        canHideColumns: false,
        canRemoveColumns: false,
        canRemoveRows: false,
        canRenameColumns: false,
        canRenameSheet: false,
        cellStates: {},
        columns: [],
        deleted_ids: [],
        initialSheetRequestLoading: true, // used to show spinner for the first request, doesn't get set to true again
        isDownloading: false,
        isMobile: false,
        isPublicSheetMode: false, // are we rendering a Public Sheet
        isReorderable: false,
        isResizable: false,
        isResourceTable: false,
        isSubmitting: false,
        loading: true,
        mapFilterValues: [],
        resourceTableConfiguration: {}, // allows us to store resource table props for Resource Tables
        row_data: [],
        row_count: 0,
        rowHeight: sheetConstants.ROW_HEIGHT,
        safedSearchFilters: [],
        scrollLeft: 0,
        scrollTop: 0,
        searchingColumnIndexes: [],
        searchingColumnValues: {},
        selectOpen: false,
        sheet: createBlankSheet(sheet || {}), // if we have args to pass in. Important for ResourceTables to work correctly
        shouldShowLoadingSpinnerOverride: false, // used to override whether the Loading Spinner is shown, bypassing initialSheetRequestLoading
        shouldDisplayMathBar: false,
        sortingColumnIndex: undefined,
        sortType: sortTypes.DESC,
        suppressEmptyState: false,
        width: sheetConstants.TABLE_WIDTH,
        visualization: { graphType: GRAPH_TYPES.Scatter, showLabels: true },
        isPersonallyPinned: [],
        created_regions: [],
        external_regions: [],
        external_custom_region_selection: null,
    }).merge(kwargs)

export const initializeColumnForm = (kwargs) =>
    Immutable.fromJS({
        batchFields: [],
        columnCategory: DjangIO.app.userdata.spreadsheet.types.ColumnCategory.field.value,
        columnType: DjangIO.app.userdata.spreadsheet.types.SheetColumnType.field.value,
        custom_data_query_operation: DjangIO.app.custom_data.manage_data.models.CustomDataQueryOperations.count.value,
        customData: {},
        customValues: {},
        legislationType: DjangIO.app.models.QuorumDataType.vote.value,
        name: "",
        projectStatType: DjangIO.app.userdata.types.ProjectStatType.user.value,
        searchifyValue: {},
        searchTerm: "",
        sourceColumns: [],
        should_force_display_as_currency: false,
        should_round_cell_value: false,
        stafferColumnType: DjangIO.app.userdata.spreadsheet.types.StafferColumnType.title.value,
        stafferFields: [],
        statisticColumnCategory: DjangIO.app.userdata.spreadsheet.types.StatisticColumnCategory.general.value,
        stats_matrix_type: DjangIO.app.stats.models.StatsMatrixType.cosponsor_to_cosponsor.value,
        temporaryState: {
            customFieldsLoading: false,
        },
        user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
        note_selection_criteria: DjangIO.app.userdata.spreadsheet.types.SheetNoteSelectionCriteria.most_recent.value,
    }).merge(kwargs)

export const initializeCustomColumnColorForm = (kwargs) =>
    Immutable.fromJS({
        submitting: false,
    })

export const initialState = Immutable.fromJS({
    // initialize an empty sheet to use
    // for the sheet form
    sheetForm: initializeSheet(),

    columnForm: initializeColumnForm(),

    customColumnColorForm: initializeCustomColumnColorForm(),

    windowWidth: window.width,
    windowHeight: window.height,
    isLoadingMathBar: false,
    // the rest of this state
    // will be populated by the ids of specific
    // sheets
})

export const isSheetForm = (url) =>
    url && ((url.includes("/edit/") && url.includes("/sheet/")) || url === urls.newSheetForm)

export const isColumnForm = (url) => url && url.includes("/sheet/") && url.includes("/add_column/")

export const getInitialState = (kwargs = {}) => initialState.merge(kwargs)

const getRowCellStatesForResourceTable = (cellStateMappingFunc, count, resource) => {
    let initialStates = {}
    // initial state of cell is the same per row (resource table cell states are dependent on resource attrs)
    const value = cellStateMappingFunc ? cellStateMappingFunc(resource) : "default"
    // resourceTable columns ids are just their order (can't be edited/removed so this should be fine)
    for (let id_ of [...Array(count).keys()]) {
        initialStates[id_] = value
    }
    return initialStates
}

const getInitialCellStatesForResourceTables = (action, sheet) => {
    const cellStates = {}
    const columns = sheet.getIn(["resourceTableConfiguration", "columns"])
    if (!columns || !action.response) {
        return cellStates
    }

    action.response.objects.forEach((resource, index) => {
        cellStates[resource.id] = getRowCellStatesForResourceTable(action.cellStateMappingFunc, columns.size, resource)
    })

    return cellStates
}

const sheetReducer = (state = initialState, action) => {
    switch (action.type) {
        // initialize a sheet, if it doesn't exist in the store, or retrieve it
        // if does
        case actionTypes.SHEET_INIT:
            if (state.get(action.id)) {
                return state
            }
            return state.set(action.id, initializeSheet(action.kwargs))

        // column form
        case actionTypes.INIT_COLUMN_FORM:
            return state.set("columnForm", initializeColumnForm(action.kwargs))
        case actionTypes.EDIT_COLUMN_FORM:
            return state.mergeIn(["columnForm"], action.payload)

        case actionTypes.TOGGLE_COLUMN_SUBMITTING:
            return state.setIn(["columnForm", "isSubmitting"], action.isSubmitting)

        case actionTypes.TOGGLE_SHEET_SUBMITTING:
            return state.setIn(["sheetForm", "isSubmitting"], action.isSubmitting)

        case actionTypes.DOWNLOAD_SHEET_CSV_START:
            return state.setIn([action.sheetKey, "isDownloading"], true)
        case actionTypes.DOWNLOAD_SHEET_CSV_SUCCESS:
        case actionTypes.DOWNLOAD_SHEET_CSV_FAIL:
            return state.setIn([action.sheetKey, "isDownloading"], false)

        // sheet form
        case actionTypes.START_SHEET_SEARCHIFY:
            return state.setIn([action.id, "searchifying"], true)
        // sheet form
        case searchifyActionTypes.SEARCHIFY_START:
            if (isSheetForm(action.origin)) {
                return state.setIn(["sheetForm", "searchifying"], true)
            }
            if (isColumnForm(action.origin)) {
                return (
                    state
                        // tell the column for that we're searchifying
                        .setIn(["columnForm", "searchifying"], true)
                )
            }
            return state

        case actionTypes.SHEET_COMPLETE_SEARCHIFY:
            return state.setIn(["sheetForm", "searchifying"], false)

        case actionTypes.COLUMN_FORM_COMPLETE_SEARCHIFY:
            return (
                state
                    // tell the column form that it is done searchifying
                    .setIn(["columnForm", "searchifying"], false)
            )

        case searchifyActionTypes.SEARCHIFY_SUBMIT:
            if (isSheetForm(action.origin)) {
                return state.update("sheetForm", (sheetForm) =>
                    sheetForm
                        .setIn(["sheet", "rows", "filters"], Immutable.fromJS(action.searchifyFilters.querysetList))
                        .setIn(["sheet", "rows", "added_ids"], Immutable.fromJS(action.searchifyFilters.addedIds))
                        .setIn(["sheet", "rows", "deleted_ids"], Immutable.fromJS(action.searchifyFilters.deletedIds)),
                )
            }

            if (isColumnForm(action.origin)) {
                return (
                    state
                        // set it as the searchify value
                        .setIn(["columnForm", "searchifyValue"], Immutable.fromJS(action.filterObj))
                )
            }
            return state

        // handle the data portion of toggling a sheet to be public
        // this logic is initiated in SheetForm when clicking the toggle button
        case actionTypes.TOGGLE_PUBLIC_SHEET:
            // if the sheet was toggled public and a public slug has not been created yet
            if (action.payload.is_public && !state.getIn(["sheetForm", "sheet", "public_slug"])) {
                return state.mergeIn([action.id, "sheet"], action.payload).mergeIn([action.id, "sheet"], {
                    public_slug: randomstring.generate({
                        length: 20,
                        charset: "alphabetic",
                    }),
                })
            } else {
                return state.mergeIn([action.id, "sheet"], action.payload)
            }

        case actionTypes.TOGGLE_PUBLIC_SHEET_PASSWORD_REQUIRED:
            return state.setIn(
                [action.id, "sheet", "passwordRequired"],
                !state.getIn([action.id, "sheet", "passwordRequired"]),
            )

        // allow users to show or hide the map in public sheets
        case actionTypes.TOGGLE_SHOW_MAP:
            return state.setIn([action.id, "sheet", "show_map"], !state.getIn([action.id, "sheet", "show_map"]))

        case actionTypes.REMOVE_SHEET_FORM_ROW_FILTER:
            return state
                .updateIn(["sheetForm", "sheet", "rows"], (rows) =>
                    rows.delete("filters").delete("deleted_ids").delete("added_ids"),
                )
                .updateIn(["sheetForm", "sheet"], (sheet) => sheet.delete("searchifyCount"))

        case actionTypes.INIT_SHEET_FORM:
            return state.set("sheetForm", initializeSheet())

        case actionTypes.CREATE_BLANK_SHEET:
            const additionalArgs = action.sheet ? { sheet: action.sheet } : {}
            return state.set(action.id, Immutable.fromJS(initializeSheet({ loading: false, ...additionalArgs })))

        case actionTypes.EDIT_SHEET:
            return state.mergeIn([action.id, "sheet"], action.payload)

        // modifying sheets
        case actionTypes.PATCH_SHEET_START:
            return state.setIn([action.id, "loading"], true)

        case actionTypes.PATCH_SHEET_SUCCESS:
            return state.mergeIn([action.id, "sheet"], action.response).setIn([action.id, "loading"], false)
        case actionTypes.PATCH_SHEET_FAIL:
            return state.setIn([action.id, "loading"], false)

        case actionTypes.PATCH_SHOULD_SHOW_MATH_SHEET_START:
            return state.set("isLoadingMathBar", true)
        case actionTypes.PATCH_SHOULD_SHOW_MATH_SHEET_SUCCESS:
            return state.set("isLoadingMathBar", false).mergeIn([action.id, "sheet"], action.response)
        case actionTypes.PATCH_SHOULD_SHOW_MATH_SHEET_FAIL:
            return state.set("isLoadingMathBar", false)

        case actionTypes.RESIZE_COLUMN:
            return state.update(action.id, (sheet) =>
                action.columnId === sheetConstants.DATUM_COLUMN_INDEX
                    ? sheet.setIn(["sheet", "datum_column_width"], action.width)
                    : sheet.update("columns", (columns) =>
                          columns.map((column) =>
                              column.get("id") === action.columnId ? column.set("width", action.width) : column,
                          ),
                      ),
            )
        case actionTypes.RENAME_COLUMN:
            return state.update(action.id, (sheet) =>
                sheet.update("columns", (columns) =>
                    columns.map((column) =>
                        column.get("id") === action.columnId ? column.set("name", action.name) : column,
                    ),
                ),
            )
        case actionTypes.TOGGLE_COLUMN_IS_HIDDEN_ON_PUBLIC:
            return state.update(action.id, (sheet) =>
                sheet.update("columns", (columns) =>
                    columns.map((column) =>
                        column.get("id") === action.columnId
                            ? column.set("should_hide_column_on_public", action.isHidden)
                            : column,
                    ),
                ),
            )
        case actionTypes.TOGGLE_COLUMN_DEFAULT_SORTING:
            return state.update(action.id, (sheet) =>
                sheet.update("columns", (columns) =>
                    // iterate over all columns. If the column is the column
                    // that was just clicked, set the sort type that was passed in.
                    // otherwise, if any other column is marked as public sorted,
                    // mark it as unsorted
                    columns.map((column) => {
                        if (column.get("id") === action.columnId) {
                            return column.set("default_sort_type", action.defaultSortType)
                        } else if (
                            column.get("id") !== action.columnId &&
                            column.get("default_sort_type") !==
                                DjangIO.app.userdata.spreadsheet.types.SheetSortType.none.value
                        ) {
                            return column.set(
                                "default_sort_type",
                                DjangIO.app.userdata.spreadsheet.types.SheetSortType.none.value,
                            )
                        }

                        return column
                    }),
                ),
            )
        case actionTypes.TOGGLE_DATUM_COLUMN_IS_HIDDEN_ON_PUBLIC:
            return state.update(action.id, (sheet) => {
                return sheet.setIn(["sheet", "show_datum_column"], action.showDatumColumn)
            })
        case actionTypes.REORDER_COLUMN:
            return state.update(action.id, (sheet) =>
                sheet
                    .delete("sortingColumnIndex")
                    .set("searchingColumnIndexes", Immutable.fromJS([]))
                    .update("columns", (columns) => {
                        // get the column that was moved
                        const movedColumn = columns.find((column) => column.get("id") === action.reorderColumn)
                        // get the columns that were not moved
                        const unmovedColumns = columns.filter((column) => column.get("id") !== action.reorderColumn)
                        // if it was moved to the front
                        if (action.columnBefore == null) {
                            // prepend to columns
                            return unmovedColumns.unshift(movedColumn).map((column, idx) => column.set("order", idx))
                        }
                        // if it was moved to the end
                        if (action.columnAfter == null) {
                            // append to columns
                            return unmovedColumns.push(movedColumn).map((column, idx) => column.set("order", idx))
                        }

                        // if there is a column that it comes after
                        if (action.columnBefore != null) {
                            // find the index of that column in the unaffected columns
                            const previousIndex = unmovedColumns.findIndex(
                                (column) => column.get("id") === action.columnBefore,
                            )
                            // insert this column after that column
                            return unmovedColumns
                                .insert(previousIndex + 1, movedColumn)
                                .map((column, idx) => column.set("order", idx))
                        }
                    }),
            )
        case actionTypes.POST_COLUMN_SUCCESS:
            return state.update(action.id, (sheet) =>
                sheet.delete("sortingColumnIndex").set("searchingColumnIndexes", Immutable.fromJS([])),
            )

        case actionTypes.GET_BIG_SHEET_START:
            return state.setIn([action.id, "loading"], true).setIn([action.id, "loadingUrl"], action.url)

        case actionTypes.GET_SHEET_START:
            return state.setIn([action.id, "loading"], true)
        case actionTypes.GET_BIG_SHEET_INTERVAL_START:
            return (
                state
                    .setIn([action.id, "loading"], true)
                    // Need a second loading variable, because the cached sheet succeeding sets loading false
                    // after this resolves, but before the success.
                    .setIn([action.id, "loadingBigSheetData"], true)
            )

        case actionTypes.GET_RESOURCE_TABLE_DATA_START:
        case actionTypes.GET_RESOURCE_TABLE_INTERVAL_START:
            return state
                .setIn([action.id, "loading"], true)
                .setIn([action.id, "loadingUrl"], action.url)
                .setIn([action.id, "resourceTableRowFiltersForRequest"], action.resourceTableRowFiltersForRequest)

        case actionTypes.GET_SHEET_FAIL:
            // Don't fail if this is just the cache request, because the uncached request will resolve soon.
            if (!action.useColumnCache) {
                return state.setIn([action.id, "loading"], false)
            }

        case actionTypes.PATCH_COLUMN_START:
        case actionTypes.PATCH_RESOURCE_TABLE_RESOURCE_START:
            return state.setIn([action.id, "loading"], true)

        case actionTypes.PATCH_COLUMN_FAIL:
        case actionTypes.PATCH_RESOURCE_TABLE_RESOURCE_FAIL:
        case actionTypes.PATCH_RESOURCE_TABLE_RESOURCE_SUCCESS:
            return state.setIn([action.id, "loading"], false)

        case actionTypes.PATCH_COLUMN_SUCCESS:
            return state
                .updateIn([action.id, "columns"], (columns) =>
                    columns.map((column) =>
                        column.get("id") === action.columnId
                            ? column.mergeIn(["data"], Immutable.fromJS(action.response))
                            : column,
                    ),
                )
                .setIn([action.id, "loading"], false)

        case actionTypes.PATCH_COLUMN_OBJECT_SUCCESS:
            // when patching the column itself, we dont need to update data, so just return stop
            // loading
            return state.setIn([action.id, "loading"], false)

        // case actionTypes.REMOVE_OUTBOX_ROW_START:
        // case actionTypes.REMOVE_OUTBOX_ROW_SUCCESS:
        case actionTypes.REMOVE_OUTBOX_ROW_FAIL:
            swal("Oops!", "There was an error deleting this draft.", "error")
            return state

        case actionTypes.GET_BIG_SHEET_SUCCESS:
        case actionTypes.GET_SHEET_SUCCESS:
            // If this was a request for the cached result, but the real result has already returned, throw it away.
            if (action.useColumnCache && !state.getIn([action.id, "loading"])) {
                return state
            }

            return state.update(action.id, (sheet) =>
                sheet
                    .mergeIn(["sheet"], Immutable.fromJS(action.response)) // useful to keep is_resouce_table boolean around
                    .set("row_data", Immutable.fromJS(action.response.row_data))
                    .set("row_count", Immutable.fromJS(action.response.row_count))
                    .set("shouldDisplayMathBar", Immutable.fromJS(action.response.should_display_math_bar))
                    .set("columns", Immutable.fromJS(action.response.columns))
                    .set("safedSearchFilters", Immutable.fromJS(action.response.rows && action.response.rows.filters))
                    .set("added_ids", Immutable.fromJS(action.response.rows && action.response.rows.added_ids))
                    .set("deleted_ids", Immutable.fromJS(action.response.rows && action.response.rows.deleted_ids))
                    .set("initialSheetRequestLoading", false)
                    .setIn(["sheet", "passwordRequired"], Boolean(action.response.public_password))
                    // If this was a request for the cache, we're still loading the non-cached data
                    // For public sheets, we don't pass useColumnCache, so this will set loading: false
                    .set("loading", Boolean(action.useColumnCache))
                    .set(
                        "created_regions",
                        Immutable.fromJS(action.response.rows && action.response.rows.created_regions),
                    ),
            )

        case actionTypes.GET_RESOURCE_TABLE_DATA_SUCCESS:
            return state.update(action.id, (sheet) =>
                sheet.get("loadingUrl") === action.url
                    ? sheet
                          .set(
                              "row_data",
                              Immutable.fromJS(
                                  action.response.objects.map((item, index) => {
                                      item.index = index
                                      return item
                                  }),
                              ),
                          )
                          .set("loading", false)
                          .set("count", Immutable.fromJS(action.response.meta.total_count))
                          .delete("loadingUrl")
                          .set("initialSheetRequestLoading", false)
                          .set("cellStates", Immutable.fromJS(getInitialCellStatesForResourceTables(action, sheet)))
                          // Once suppressEmptyState is set, it can not be unset until INIT_SHEET_FORM is called.
                          // Prevents the empty state from briefly rendering after removing a filter.
                          .set(
                              "suppressEmptyState",
                              !sheet.get("suppressEmptyState") ? Boolean(action.response.objects.length) : true,
                          )
                    : sheet,
            )

        case actionTypes.GET_RESOURCE_TABLE_DATA_FAIL:
            return state.setIn([action.id, "loading"], false).setIn([action.id, "loadingUrl"], null)

        case actionTypes.GET_BIG_SHEET_INTERVAL_SUCCESS:
            return state.update(action.id, (sheet) =>
                sheet
                    .set("loading", false)
                    .set("loadingBigSheetData", false)
                    .update("row_data", (rows) => rows.concat(Immutable.fromJS(action.response.row_data)))
                    .update("columns", (columns) =>
                        columns.map((column, i) =>
                            column.update("data", (data) => data.mergeDeep(action.response.columns[i].data)),
                        ),
                    ),
            )

        case actionTypes.GET_RESOURCE_TABLE_INTERVAL_SUCCESS:
            return state.update(action.id, (sheet) =>
                sheet
                    .set("loading", false)
                    .update("row_data", (rows) => rows.concat(Immutable.fromJS(action.response.objects)))
                    .update("cellStates", (cellStates) =>
                        Immutable.fromJS(
                            Object.assign(getInitialCellStatesForResourceTables(action, sheet), cellStates.toJS()),
                        ),
                    ),
            )

        case actionTypes.EDIT_COLUMN:
            return state.update(action.id, (sheet) =>
                sheet.update("columns", (columns) =>
                    columns.map((column) =>
                        // if this is the correct column
                        column.get("id") === action.columnId
                            ? // set the new value
                              column.setIn(["data", String(action.rowId)], Immutable.fromJS(action.newValue))
                            : // otherwise don;t
                              column,
                    ),
                ),
            )

        case actionTypes.SCROLL_TABLE:
            return state.set("scrollLeft", action.scrollLeft).set("scrollTop", action.scrollTop)

        case actionTypes.CLICK_CELL:
            return state.update(action.id, (sheet) =>
                sheet.set("clickedRow", action.rowId).set("clickedColumn", action.columnId),
            )

        // when removing a row, remove the row from UI optimistically
        case actionTypes.RESOURCE_TABLE_REMOVE_ROW: {
            // we have to rely on the datumId provided, since the rowIndex of a row can change
            // depending on filtering/sorting conditions
            const datumIndex = state
                .getIn([action.sheetKey, "row_data"])
                .findIndex((row) => row.get("id") === action.datumId)
            return state.updateIn([action.sheetKey, "row_data"], (rowData) => rowData.delete(datumIndex))
        }

        case actionTypes.RESOURCE_TABLE_RESTORE_ROW: {
            return state.updateIn([action.sheetKey, "row_data"], (rowData) => rowData.push(action.row))
        }

        case actionTypes.SHEET_REMOVE_ROW: {
            // we have to rely on the datumId provided, since the rowIndex of a row can change
            // depending on filtering/sorting conditions
            const datumIndex = state.getIn([action.id, "row_data"]).findIndex((row) => row.get("id") === action.datumId)

            // once we've found the correct datumIndex, we can delete the row from rowData
            return state.update(action.id, (sheet) => sheet.update("row_data", (rowData) => rowData.delete(datumIndex)))
        }

        // find the row that we wish to update, and merge in the updateArgs into the row
        case actionTypes.RESOURCE_TABLE_UPDATE_ROW: {
            const { sheetKey, datumId, updateArgs, cellStateMappingFunc } = action

            // we have to rely on the datumId provided, since the rowIndex of a row can change
            // depending on filtering/sorting conditions
            const datumIndex = state.getIn([sheetKey, "row_data"]).findIndex((row) => row.get("id") === datumId)

            // Merge the new data into the current data, and if necessary separately merge _extra
            const currentRow = state.getIn([sheetKey, "row_data", datumIndex])
            const updateData = fromJS(updateArgs)
            let mergedData = currentRow.merge(updateData)
            if (mergedData.has("_extra") && updateData) {
                mergedData = mergedData.set("_extra", currentRow.get("_extra").merge(updateData.get("_extra")))
            }

            return state.setIn([sheetKey, "row_data", datumIndex], mergedData).mergeIn(
                [sheetKey, "cellStates", String(datumId)],
                Immutable.fromJS(
                    getRowCellStatesForResourceTable(
                        cellStateMappingFunc,
                        //can trust that number of columns is the same for resourceTables
                        state.getIn([sheetKey, "resourceTableConfiguration", "columns"])
                            ? state.getIn([sheetKey, "resourceTableConfiguration", "columns"]).size
                            : 0,
                        mergedData.toJS(),
                    ),
                ),
            )
        }

        //updated the rowData array to match with what we send to the backend
        case actionTypes.RESOURCE_TABLE_UPDATE: {
            const { sheetKey, toUpdate } = action
            let rowData = state.getIn([sheetKey, "row_data"]).toJS()

            rowData = rowData.map((rowItem) => {
                const matchingUpdate = toUpdate.find((updateItem) => updateItem.resource_uri === rowItem.resource_uri)
                return matchingUpdate ? { ...rowItem, ...matchingUpdate } : rowItem
            })

            return state.setIn([sheetKey, "row_data"], Immutable.fromJS(rowData))
        }

        // moves an item on row_data from currentPlace (index) to newPlace (desired index)
        case actionTypes.RESOURCE_TABLE_REORDER: {
            const { sheetKey, datumId, newPlace, currentPlace } = action
            const rowData = state.getIn([sheetKey, "row_data"]).toJS()
            // remove and save the item at currentPlace
            const toReorder = rowData[currentPlace]
            rowData.splice(currentPlace, 1)
            // re-insert it at newPlace
            rowData.splice(newPlace, 0, toReorder)
            return state.setIn([sheetKey, "row_data"], Immutable.fromJS(rowData))
        }

        case actionTypes.RESOURCE_TABLE_CLEAR_ALL_ROWS: {
            return state
                .setIn([action.sheetKey, "row_data"], Immutable.fromJS([]))
                .setIn([action.sheetKey, "initialSheetRequestLoading"], true)
        }

        // set the added_ids after we delete a row
        // this is not a typo, we MUST reset added_ids because added_ids intersect deleted_ids
        // should the empty set
        case actionTypes.SHEET_UPDATE_ADDED_IDS:
            return state.update(action.id, (sheet) =>
                sheet.setIn(["sheet", "rows", "added_ids"], action.updatedAddedIds),
            )

        // set the deleted_ids after we delete a row
        case actionTypes.SHEET_UPDATE_DELETED_IDS:
            return state.update(action.id, (sheet) =>
                sheet.setIn(["sheet", "rows", "deleted_ids"], action.updatedDeletedIds),
            )

        case actionTypes.SHEET_REMOVE_COLUMN:
            // here we remove the column that the user deleted, but we also remove all columns that have
            // the removed column as the parent column
            return state.update(action.id, (sheet) =>
                sheet
                    .update("columns", (columns) =>
                        columns.filter(
                            (column) => ![column.get("id"), column.get("parent_column_id")].includes(action.columnId),
                        ),
                    )
                    .updateIn(["sheet", "columns"], (columns) =>
                        columns.filter(
                            (column) => ![column.get("id"), column.get("parent_column_id")].includes(action.columnId),
                        ),
                    )
                    .delete("sortingColumnIndex")
                    .set("searchingColumnIndexes", Immutable.fromJS([])),
            )

        case actionTypes.SORT_BY_COLUMN:
            return state.update(action.id, (sheet) =>
                sheet.set("sortingColumnIndex", action.columnIndex).set("sortType", action.sortType),
            )

        // add a region enum value to the currently applied filters
        case actionTypes.ADD_MAP_FILTER:
            return state.update(action.id, (sheet) =>
                sheet.update("mapFilterValues", (mapFilterValues) => mapFilterValues.push(action.value)),
            )

        // remove a region enum value from the currently applied filters
        case actionTypes.REMOVE_MAP_FILTER:
            return state.update(action.id, (sheet) =>
                sheet.update("mapFilterValues", (mapFilterValues) =>
                    mapFilterValues.delete(mapFilterValues.indexOf(action.value)),
                ),
            )

        case actionTypes.SEARCH_BY_COLUMN:
            return state.update(action.id, (sheet) =>
                sheetConstants.FALSEY_VALUES.includes(action.value) || isEmptyDateSearch(action.value)
                    ? sheet
                          .update("searchingColumnIndexes", (indexes) =>
                              indexes.filterNot((each) => each === action.columnIndex),
                          )
                          .update("searchingColumnValues", (values) => values.delete(String(action.columnIndex)))
                    : sheet
                          .update("searchingColumnIndexes", (indexes) =>
                              indexes.includes(action.columnIndex) ? indexes : indexes.push(action.columnIndex),
                          )
                          .setIn(["searchingColumnValues", String(action.columnIndex)], Immutable.fromJS(action.value)),
            )
        case actionTypes.FOCUS_COLUMN:
            return state.update(action.id, (sheet) => sheet.set("focusedColumnIndex", action.columnIndex))
        case actionTypes.UNFOCUS_COLUMN:
            return state.update(action.id, (sheet) =>
                sheet.get("focusedColumnIndex") === action.columnIndex ? sheet.delete("focusedColumnIndex") : sheet,
            )

        case actionTypes.SHEET_CLEAR_FILTER_AND_SORT:
            return state.update(action.id, (sheet) =>
                sheet
                    .delete("sortingColumnIndex")
                    .delete("sortType")
                    .set("searchingColumnIndexes", Immutable.fromJS([]))
                    .set("searchingColumnValues", Immutable.fromJS({})),
            )

        // only clear filtering
        case actionTypes.SHEET_CLEAR_FILTER:
            return state.update(action.id, (sheet) =>
                sheet
                    .update("searchingColumnIndexes", (indexes) =>
                        indexes.filterNot((each) => each === action.columnIndex),
                    )
                    .update("searchingColumnValues", (values) => values.delete(String(action.columnIndex))),
            )

        // only clear sorting
        case actionTypes.SHEET_CLEAR_SORT:
            return state.update(action.id, (sheet) => sheet.delete("sortingColumnIndex").delete("sortType"))

        // set the value of selectOpen depending on if the select is open
        case actionTypes.TOGGLE_SELECT_OPEN:
            return state.update(action.id, (sheet) => sheet.set("selectOpen", action.selectIsOpen))

        case actionTypes.MERGE_VIS_DICT:
            return state.update(action.id, (sheet) => sheet.mergeIn(["visualization"], action.dict))

        case actionTypes.STORE_RESOURCE_TABLE_CONFIGURATION:
            return state.mergeIn(
                [action.id, "resourceTableConfiguration"],
                Immutable.fromJS(action.resourceTableConfiguration),
            )

        case actionTypes.UPDATE_CELL_STATE:
            return state.update(action.id, (sheet) =>
                sheet.setIn(["cellStates", String(action.rowId), String(action.columnId)], action.state),
            )

        case actionTypes.RESET_CELL_STATE:
            return state.update(action.id, (sheet) =>
                sheet.setIn(["cellStates", String(action.rowId), String(action.columnId)], "default"),
            )

        case actionTypes.UPDATE_RESOURCE_TABLE_CELL_STATE:
            return state.update(action.id, (sheet) => {
                const { columnKey, rowId, state } = action
                const columns = sheet.getIn(["resourceTableConfiguration", "columns"]).slice()
                let columnId
                columns.map((each, index) => {
                    let field = each.get("field")
                    if ((field && field.size && field.toJS().includes(columnKey)) || field === columnKey) {
                        columnId = index
                    }
                })
                return sheet.setIn(["cellStates", String(rowId), String(columnId)], state)
            })

        case actionTypes.SET_WINDOW_SIZE:
            return state.set("windowHeight", action.height).set("windowWidth", action.width)

        case actionTypes.PATCH_COLUMN_FOR_CUSTOM_COLOR_FORM_START:
            return state.setIn(["customColumnColorForm", "submitting"], true)

        case actionTypes.PATCH_COLUMN_FOR_CUSTOM_COLOR_FORM_SUCCESS:
            // update the column in the store so that the custom color form
            // is correctly initialized if it is visited again before the
            // store is refreshed from the database

            // expect action.response.sheet to be of the form "/api/sheet/[id]"
            const sheetId = idFromResourceUri(action.response.sheet)
            const sheetKey = createSheetKey(sheetId)

            const columnIndex = state.getIn([sheetKey, "columns"]).findIndex((col) => {
                return col.get("id") === action.response.id
            })

            const path = [sheetKey, "columns", columnIndex, "custom_color_settings"]
            return state
                .setIn(path, Immutable.fromJS(action.response.custom_color_settings))
                .setIn(["customColumnColorForm", "submitting"], false)

        case actionTypes.PATCH_COLUMN_FOR_CUSTOM_COLOR_FORM_FAIL:
            state.setIn(["customColumnColorForm", "submitting"], false)
            return state.setIn(["customColumnColorForm", "errorSubmitting"], true)

        case customFieldActionTypes.GET_CUSTOM_FIELDS_START:
            return state.setIn(["columnForm", "temporaryState", "customFieldsLoading"], true)
        case customFieldActionTypes.GET_CUSTOM_FIELDS_FAIL:
            return state.setIn(["columnForm", "temporaryState", "customFieldsLoading"], false)
        case customFieldActionTypes.GET_CUSTOM_FIELDS_SUCCESS:
            return state
                .setIn(["columnForm", "customFields"], Immutable.fromJS(action.response))
                .setIn(["columnForm", "temporaryState", "customFieldsLoading"], false)

        case actionTypes.SET_SHOULD_SHOW_STATS_BAR:
            return state.setIn([action.sheetKey, "shouldDisplayMathBar"], action.showStats)
        case actionTypes.PERSONAL_PIN_SHEET_FOR_USER_START:
            return state.setIn(["sheetForm", "loading"], true)
        case actionTypes.PERSONAL_PIN_SHEET_FOR_USER_FAIL:
            return state.setIn(["sheetForm", "loading"], false)
        case actionTypes.PERSONAL_PIN_SHEET_FOR_USER_SUCCESS:
            return state
                .setIn(["sheetForm", "isPersonallyPinned"], action.response === "True")
                .setIn(["sheetForm", "loading"], false)

        default:
            return state
    }
}

export default sheetReducer
